/**
 * Shared map and marker utility primitives.
 *
 * Why this file exists:
 * - Prevents duplication of low-level math/conversion logic across plugin and marker modules.
 * - Keeps pure helpers centralized so behavior is consistent in all map flows.
 *
 * Responsibilities:
 * - Coordinate and bounds conversions (pure/plain-object only).
 * - Spatial/hash key generation.
 * - Camera snapshot comparison.
 *
 * Note: Functions that consume live Google Maps SDK objects (LatLngBounds, Map) live in
 * mapSnapshot.js, not here. This file must remain free of SDK coupling.
 */
/**
 * Cell size (in decimal degrees) used to bucket map positions into a spatial grid.
 * A smaller value produces finer-grained cells; a larger value produces coarser cells.
 * Used by {@link getSpatialKey} to generate consistent grid keys for nearby coordinates.
 */
export const GRID_SIZE = 0.05;

/**
 * Minimum latitude/longitude span (in decimal degrees) applied when computing padding
 * in {@link expandBounds}, preventing degenerate bounds on highly-zoomed or point views.
 */
export const MIN_SPAN = 0.01;
export function getGroupKey(position, zoom = 12) {
  if (zoom >= 13) {
    // At high zoom, keep per-marker identity (no coordinate bucketing).
    return `${position.lat},${position.lng}`;
  }

  let precision;
  if (zoom < 9) {
    precision = 10;
  } else if (zoom < 10) {
    precision = 50;
  } else {
    precision = 100;
  }

  const latKey = Math.floor(position.lat * precision);
  const lngKey = Math.floor(position.lng * precision);

  return `${latKey},${lngKey}`;
}

export function normalizeLongitude(value) {
  if (value > 180) {
    return value - 360;
  }

  if (value < -180) {
    return value + 360;
  }

  return value;
}

export function expandBounds(bounds, paddingRatio) {
  if (!bounds) {
    return null;
  }

  if (paddingRatio <= 0) {
    return bounds;
  }

  const latitudeSpan = Math.max(MIN_SPAN, bounds.north - bounds.south);
  const longitudeSpan = bounds.east >= bounds.west
    ? Math.max(MIN_SPAN, bounds.east - bounds.west)
    : Math.max(MIN_SPAN, 360 - bounds.west + bounds.east);
  const latitudePadding = latitudeSpan * paddingRatio;
  const longitudePadding = longitudeSpan * paddingRatio;

  return {
    north: bounds.north + latitudePadding,
    south: bounds.south - latitudePadding,
    east: normalizeLongitude(bounds.east + longitudePadding),
    west: normalizeLongitude(bounds.west - longitudePadding),
  };
}

export function isWithinBounds(position, bounds) {
  if (!bounds) {
    return true;
  }

  const isWithinLatitude = position.lat <= bounds.north && position.lat >= bounds.south;
  const isWithinLongitude = bounds.west <= bounds.east
    ? position.lng >= bounds.west && position.lng <= bounds.east
    : position.lng >= bounds.west || position.lng <= bounds.east;

  return isWithinLatitude && isWithinLongitude;
}

export function getSpatialKey(position, gridSize = GRID_SIZE) {
  const latKey = Math.floor(position.lat / gridSize);
  const lngKey = Math.floor(position.lng / gridSize);

  return `${latKey},${lngKey}`;
}

export function isSameMarkerPosition(left, right) {
  if (!left || !right) {
    return false;
  }

  return left.lat === right.lat && left.lng === right.lng;
}
